import React, { useState, useRef, useEffect } from "react";
import IntegrationsService from "../../services/Integrations/IntegrationsService";
import { getZohoStatus } from "../../services/Integrations/getZohoStatus";
import { integrationDefinitions, getInitialIntegrations, getRemainingIntegrations } from "../../services/Integrations/integrationConfig";
import { IntegrationStatus, parseBackendIntegrationName } from "../../services/Integrations/types";
import IntegrationCard from "../../components/IntegrationCard/IntegrationCard";

const integrationsService = new IntegrationsService();

/**
 * Modal component for confirmations and OAuth flow
 */
function Modal({
	title,
	children,
	onClose,
	onConfirm,
	confirmLabel = "Yes",
	isLoading = false,
}) {
	return (
		<div
			className="modal-backdrop"
			role="dialog"
			aria-modal="true"
			aria-labelledby={title ? "modal-title" : undefined}
			tabIndex="-1"
			onClick={(e) => e.target === e.currentTarget && onClose()}
		>
			<div className="modal" role="document">
				{title && <h3 id="modal-title">{title}</h3>}
				<div className="modal-content">{children}</div>
				<div className="modal-actions">
					<button 
						onClick={onConfirm} 
						className="btn btn-primary"
						disabled={isLoading}
					>
						{isLoading ? "Loading..." : confirmLabel}
					</button>
					<button 
						onClick={onClose} 
						className="btn btn-secondary"
						disabled={isLoading}
					>
						Cancel
					</button>
				</div>
			</div>
		</div>
	);
}

/**
 * OAuth authorization modal - generic for all providers and products
 */
function OAuthModal({ integration, onClose, onConfirm, isLoading }) {
	const iconSvg = (
		<svg width="56" height="56" viewBox="0 0 56 56" fill="none" xmlns="http://www.w3.org/2000/svg">
			<rect width="56" height="56" rx="12" fill="#F0F0F5"/>
			<rect x="16" y="18" width="6" height="20" rx="2" fill="#5C60F5"/>
			<rect x="25" y="12" width="6" height="26" rx="2" fill="#3AC1C9"/>
			<rect x="34" y="24" width="6" height="14" rx="2" fill="#A0AEC0"/>
		</svg>
	);

	const permissionsText = {
		"ZOHO_BOOKS": [
			"Read your financial and business data",
			"Sync data automatically for real-time insights",
			"Generate AI-powered reports and analytics",
		],
		"ZOHO_PROJECTS": [
			"Read your projects, tasks, and milestones",
			"Access project analytics and timesheets",
			"Generate AI-powered project insights and reports",
		],
	};

	const key = `${integration.provider}_${integration.product}`;
	const permissions = permissionsText[key] || permissionsText["ZOHO_BOOKS"];

	return (
		<div className="zoho-modal-container">
			<div className="zoho-modal-header">
				<span className="zoho-modal-title">Connect to {integration.name}</span>
			</div>
			<div className="zoho-modal-icon-wrapper">
				<span className="zoho-modal-icon" aria-hidden="true">
					{iconSvg}
				</span>
			</div>
			<div className="zoho-modal-auth-title">Authorize MiFlow</div>
			<div className="zoho-modal-desc">MiFlow would like permission to access your {integration.name} account.</div>
			<div className="zoho-modal-infobox">
				<div className="zoho-modal-infobox-icon">🔒</div>
				<div>
					<div className="zoho-modal-infobox-title">Your data is secure</div>
					<div className="zoho-modal-infobox-desc">
						MiFlow uses industry-standard OAuth 2.0 authentication. We never store your login credentials.
					</div>
				</div>
			</div>
			<div className="zoho-modal-allow-title">This will allow MiFlow to:</div>
			<ul className="zoho-modal-allow-list">
				{permissions.map((perm, idx) => (
					<li key={idx}>{perm}</li>
				))}
			</ul>
		</div>
	);
}

/**
 * Main Integrations page - data-driven architecture
 */
export default function Integrations() {
	// State management
	const [statuses, setStatuses] = useState({}); // Map of integration ID to status
	const [showMore, setShowMore] = useState(false);
	const [modal, setModal] = useState(null); // { type, integration }
	const [loadingIntegration, setLoadingIntegration] = useState(null); // Track which integration is loading
	const [notificationMessage, setNotificationMessage] = useState(null);
	const [notificationError, setNotificationError] = useState(null);
	const accordionRef = useRef(null);
	const [accordionHeight, setAccordionHeight] = useState("0px");

	// Split integrations for initial display and "more"
	const firstThree = getInitialIntegrations(3);
	const others = getRemainingIntegrations(3);

	// Accordion height effect
	useEffect(() => {
		if (showMore && accordionRef.current) {
			setAccordionHeight(`${accordionRef.current.scrollHeight}px`);
		} else {
			setAccordionHeight("0px");
		}
	}, [showMore]);

	// Fetch integration status on component mount
	useEffect(() => {
		async function fetchStatuses() {
			try {
				const statusArr = await getZohoStatus();
				
				// Build a map of integration IDs to their current status
				const statusMap = {};
				
				statusArr.forEach(item => {
					// Parse the backend integration name
					const parsed = parseBackendIntegrationName(item.integrationName);
					if (parsed) {
						// Find the matching integration definition
						const integration = integrationDefinitions.find(
							def => def.provider === parsed.provider && def.product === parsed.product
						);
						if (integration) {
							statusMap[integration.id] = item.connected ? IntegrationStatus.CONNECTED : IntegrationStatus.NOT_CONNECTED;
						}
					}
				});

				setStatuses(statusMap);
			} catch (err) {
				console.error("Failed to fetch integration statuses:", err);
				// Initialize all statuses to not connected on error
				const initialStatuses = {};
				integrationDefinitions.forEach(def => {
					initialStatuses[def.id] = IntegrationStatus.NOT_CONNECTED;
				});
				setStatuses(initialStatuses);
			}
		}

		fetchStatuses();
	}, []);

	// Notification management
	const showNotification = (message, isError = false) => {
		if (isError) {
			setNotificationError(message);
			setNotificationMessage(null);
		} else {
			setNotificationMessage(message);
			setNotificationError(null);
		}
		
		// Auto-dismiss notification after 5 seconds
		setTimeout(() => {
			setNotificationMessage(null);
			setNotificationError(null);
		}, 5000);
	};

	// Close all modals
	const closeModal = () => setModal(null);

	// Handle connect button click
	const handleConnectClick = (integration) => {
		if (!integration.connectable) {
			setModal({ type: "comingSoon", integration });
			return;
		}

		if (statuses[integration.id] === IntegrationStatus.CONNECTED) {
			setModal({ type: "confirmDisconnect", integration });
		} else {
			setModal({ type: "connect", integration });
		}
	};

	// Handle disconnect button click
	const handleDisconnectClick = (integration) => {
		setModal({ type: "confirmDisconnect", integration });
	};

	// Handle modal confirmation
	const handleConfirm = async () => {
		const { integration, type } = modal;

		if (type === "confirmDisconnect") {
			try {
				setLoadingIntegration(integration.id);
				await integrationsService.disconnectIntegration(
					integration.provider,
					integration.product
				);
				
				// Update local state
				setStatuses(prev => ({
					...prev,
					[integration.id]: IntegrationStatus.NOT_CONNECTED
				}));
				
				showNotification(`${integration.name} disconnected successfully`);
			} catch (err) {
				console.error("Failed to disconnect integration:", err);
				showNotification(`Failed to disconnect ${integration.name}`, true);
			} finally {
				setLoadingIntegration(null);
				closeModal();
			}
		} else if (type === "connect") {
			try {
				setLoadingIntegration(integration.id);
				setStatuses(prev => ({
					...prev,
					[integration.id]: IntegrationStatus.PENDING
				}));

				// Determine which connect method to call based on product
				let authUrlResult;
				
				if (integration.product === "BOOKS") {
					authUrlResult = await integrationsService.zohoConnect(
						getCallbackUrl(integration)
					);
				} else if (integration.product === "PROJECTS") {
					authUrlResult = await integrationsService.zohoProjectsConnect(
						getCallbackUrl(integration)
					);
				} else {
					throw new Error(`Unknown product: ${integration.product}`);
				}

				// Redirect to OAuth URL
				if (authUrlResult?.authorizationUrl) {
					window.location.href = authUrlResult.authorizationUrl;
				} else {
					throw new Error("No authorization URL received");
				}
			} catch (err) {
				console.error("Failed to initiate connection:", err);
				setStatuses(prev => ({
					...prev,
					[integration.id]: IntegrationStatus.ERROR
				}));
				showNotification(`Failed to connect ${integration.name}: ${err.message}`, true);
				setLoadingIntegration(null);
				closeModal();
			}
		}
	};

	// Get callback URL for specific integration
	const getCallbackUrl = (integration) => {
		// Use frontend domain as the callback URL
		// Zoho redirects the browser here, then the frontend ZohoCallback component calls the API with Cognito token
		const domain = process.env.DOMAIN_NAME || (process.env.REACT_APP_ENVIRONMENT === "pro"
			? "app.miflow.io"
			: "app.dev.miflow.io");
		
		// Use HTTPS for pro, HTTP for dev
		const scheme = process.env.REACT_APP_ENVIRONMENT === "pro" ? "https" : "http";
		
		// Use product-specific callback URLs for different products
		if (integration.product === "PROJECTS") {
			return `${scheme}://${domain}/integrations/zoho/projects/callback`;
		}
		return `${scheme}://${domain}/integrations/zoho/callback`;
	};

	// Get status for integration
	const getIntegrationStatus = (integration) => {
		return statuses[integration.id] || IntegrationStatus.NOT_CONNECTED;
	};

	return (
		<main className="integrations-container" role="main">
			{/* Notification Messages */}
			{notificationMessage && (
				<div className="notification notification-success">
					<span>{notificationMessage}</span>
				</div>
			)}
			{notificationError && (
				<div className="notification notification-error">
					<span>{notificationError}</span>
				</div>
			)}

			{/* Header Section */}
			<section className="header-section">
				<h1 tabIndex={-1}>Integrations</h1>
				<p className="subtitle">
					Connect your ERP, spreadsheets, and project management tools to sync
					your business data and improve AI insights.
				</p>
			</section>

			<h3 className="subheader">ERP Integrations</h3>

			{/* Initial 3 Integrations Grid */}
			<section className="cards-grid" aria-label="Initial ERP Integrations">
				{firstThree.map(integration => (
					<IntegrationCard
						key={integration.id}
						integration={integration}
						status={getIntegrationStatus(integration)}
						onConnect={handleConnectClick}
						onDisconnect={handleDisconnectClick}
						isLoading={loadingIntegration === integration.id}
					/>
				))}
			</section>

			{/* More Button */}
			{others.length > 0 && (
				<div className="btn-more-container">
					{!showMore && (
						<button
							className="btn btn-more"
							onClick={() => setShowMore(true)}
							aria-expanded={showMore}
							aria-controls="more-integrations"
						>
							More ({others.length})
						</button>
					)}
				</div>
			)}

			{/* Additional Integrations (Accordion) */}
			{others.length > 0 && (
				<section
					id="more-integrations"
					className="cards-grid accordion"
					style={{ maxHeight: accordionHeight }}
					ref={accordionRef}
					aria-hidden={!showMore}
					aria-label="Additional ERP Integrations"
				>
					{others.map(integration => (
						<IntegrationCard
							key={integration.id}
							integration={integration}
							status={getIntegrationStatus(integration)}
							onConnect={handleConnectClick}
							onDisconnect={handleDisconnectClick}
							isLoading={loadingIntegration === integration.id}
						/>
					))}
				</section>
			)}

			{/* Confirm Disconnect Modal */}
			{modal?.type === "confirmDisconnect" && (
				<Modal
					title="Are you sure?"
					onClose={closeModal}
					onConfirm={handleConfirm}
					confirmLabel="Disconnect"
					isLoading={loadingIntegration !== null}
				>
					<p>
						Are you sure you want to disconnect{" "}
						<strong>{modal.integration.name}</strong>?
					</p>
				</Modal>
			)}

			{/* OAuth Connect Modal */}
			{modal?.type === "connect" && (
				<Modal
					title={null}
					onClose={closeModal}
					onConfirm={handleConfirm}
					confirmLabel="Authorize"
					isLoading={loadingIntegration !== null}
				>
					<OAuthModal 
						integration={modal.integration}
						onClose={closeModal}
						onConfirm={handleConfirm}
						isLoading={loadingIntegration !== null}
					/>
				</Modal>
			)}

			{/* Coming Soon Modal */}
			{modal?.type === "comingSoon" && (
				<Modal
					title="Coming Soon"
					onClose={closeModal}
					onConfirm={closeModal}
					confirmLabel="OK"
				>
					<p>
						{modal.integration.name} integration is not available yet. Please check back later!
					</p>
				</Modal>
			)}

      <style>{`
        /* Notification Styles */
        .notification {
          position: fixed;
          top: 1.5rem;
          right: 1.5rem;
          padding: 1rem 1.5rem;
          border-radius: 8px;
          font-size: 0.95rem;
          font-weight: 500;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
          z-index: 1200;
          animation: slideIn 0.3s ease-out;
        }

        .notification-success {
          background-color: #d4f4d1;
          color: #3a933a;
          border-left: 4px solid #3a933a;
        }

        .notification-error {
          background-color: #fee2e2;
          color: #dc2626;
          border-left: 4px solid #dc2626;
        }

        @keyframes slideIn {
          from {
            transform: translateX(400px);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }

        /* Modal OAuth Container */
        .zoho-modal-container {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 0.5rem 0.5rem 0.5rem 0.5rem;
          min-width: 320px;
          width: 100%;
        }
        .zoho-modal-header {
          width: 100%;
          display: flex;
          justify-content: center;
          margin-bottom: 0.5rem;
        }
        .zoho-modal-title {
          font-size: 1.35rem;
          font-weight: 600;
          color: #22223b;
          margin-bottom: 0.5rem;
          text-align: center;
          width: 100%;
        }
        .zoho-modal-icon-wrapper {
          margin: 0.5rem 0 0.5rem 0;
          display: flex;
          justify-content: center;
          width: 100%;
        }
        .zoho-modal-icon {
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .zoho-modal-auth-title {
          font-size: 1.1rem;
          font-weight: 600;
          margin-top: 0.5rem;
          margin-bottom: 0.2rem;
          color: #22223b;
          text-align: center;
          width: 100%;
        }
        .zoho-modal-desc {
          font-size: 1rem;
          color: #4a5568;
          margin-bottom: 0.7rem;
          text-align: center;
          width: 100%;
        }
        .zoho-modal-infobox {
          display: flex;
          align-items: flex-start;
          background: #f6faff;
          border: 1.5px solid #b3d4fc;
          border-radius: 10px;
          padding: 0.7rem 1rem;
          margin-bottom: 1.1rem;
          width: 100%;
          gap: 0.7rem;
          box-sizing: border-box;
          max-width: 340px;
        }
        .zoho-modal-infobox-icon {
          font-size: 1.5rem;
          margin-top: 0.1rem;
          color: #f59e42;
        }
        .zoho-modal-infobox-title {
          font-weight: 600;
          color: #2563eb;
          font-size: 1rem;
        }
        .zoho-modal-infobox-desc {
          font-size: 0.95rem;
          color: #4a5568;
        }
        .zoho-modal-allow-title {
          font-size: 1rem;
          font-weight: 600;
          color: #22223b;
          margin-bottom: 0.3rem;
          margin-top: 0.2rem;
          align-self: flex-start;
          width: 100%;
        }
        .zoho-modal-allow-list {
          font-size: 0.98rem;
          color: #4a5568;
          margin: 0 0 0.2rem 0;
          padding-left: 1.2rem;
          align-self: flex-start;
          width: 100%;
        }
        .zoho-modal-allow-list li {
          margin-bottom: 0.2rem;
        }

        /* Main Container */
        .integrations-container {
          display: flex;
          flex-direction: column;
          padding: 2rem 3rem 3rem;
          height: 100vh;
          overflow-y: auto;
          box-sizing: border-box;
          background: #f9fafb;
        }

        .header-section {
          margin-bottom: 1rem;
        }

        .subtitle {
          color: #4a5568;
          font-size: 1rem;
          max-width: 720px;
          line-height: 1.5;
        }

        .subheader {
          font-weight: 600;
          margin-bottom: 1rem;
          margin-top: 2rem;
          font-size: 1.25rem;
          color: #2d3748;
        }

        .cards-grid {
          display: flex;
          flex-wrap: wrap;
          gap: 1.5rem;
        }

        .btn-more-container {
          width: calc((320px * 3) + (1.5rem * 2));
          margin-top: 1.5rem;
          display: flex;
          justify-content: flex-start;
        }

        .btn-more {
          padding: 0.5rem 1.25rem;
          font-size: 0.9rem;
          font-weight: 600;
          border-radius: 8px;
          border: none;
          background-color: #5c60f5;
          color: white;
          cursor: pointer;
          width: auto;
          min-width: 100px;
          box-sizing: border-box;
          transition: background-color 0.2s ease;
        }

        .btn-more:hover {
          background-color: #4a4fc1;
        }

        .accordion {
          overflow: hidden;
          transition: max-height 0.4s ease;
          flex-wrap: wrap;
          display: flex;
          gap: 1.5rem;
          margin-top: 1.5rem;
        }

        /* Modal Styles */
        .modal-backdrop {
          position: fixed;
          inset: 0;
          background: rgba(0, 0, 0, 0.35);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1100;
          padding: 1rem;
        }

        .modal {
          background: white;
          border-radius: 12px;
          box-shadow: 0 0 10px rgba(0, 0, 0, 0.2);
          max-width: 400px;
          width: 100%;
          padding: 2rem 2rem 1.5rem 2rem;
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 1.25rem;
        }

        .modal h3 {
          margin: 0;
          font-size: 1.25rem;
          color: #2d3748;
          text-align: center;
        }

        .modal-content {
          font-size: 1rem;
          color: #2d3748;
          width: 100%;
          display: flex;
          flex-direction: column;
          align-items: center;
        }

        .modal-content p {
          margin: 0;
          text-align: center;
        }

        .modal-actions {
          display: flex;
          justify-content: center;
          gap: 0.75rem;
          width: 100%;
        }

        .btn {
          font-weight: 600;
          border-radius: 8px;
          border: none;
          padding: 0.55rem 1.2rem;
          cursor: pointer;
          font-size: 0.9rem;
          transition: all 0.2s ease;
        }

        .btn:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .btn-primary {
          background: #5c60f5;
          color: white;
        }
        .btn-primary:hover:not(:disabled) {
          background: #484dca;
        }

        .btn-secondary {
          background: transparent;
          color: #6b7280;
        }
        .btn-secondary:hover:not(:disabled) {
          color: #3b82f6;
          background: #f0f9ff;
        }

        .btn-coming-soon {
          background-color: #e2e8f0;
          color: #6b7280;
          cursor: not-allowed;
        }

        .btn-coming-soon:hover {
          background-color: #e2e8f0;
        }

        /* Responsive Design */
        @media (max-width: 768px) {
          .integrations-container {
            padding: 1.5rem 1.5rem 2rem;
            height: auto;
          }

          .cards-grid,
          .accordion {
            justify-content: center;
          }

          .btn-more-container {
            width: 100%;
            justify-content: center;
          }

          .notification {
            left: 1rem;
            right: 1rem;
            max-width: calc(100% - 2rem);
          }
        }
      `}</style>
		</main>
	);
}